/** Badges and chips — design spec §7. */

import type { ReactNode } from "react";
import {
  CircleCheckBig,
  Clock,
  Paperclip,
  TriangleAlert,
} from "lucide-react";

import type { IdeaStatus, StatusCategory, ToolKind } from "@/api/types";
import { Mark, Status } from "@/components/ui/Status";
import { cn } from "@/lib/cn";
import { daysUntil, formatDayMonth, pluralRu } from "@/lib/format";

/**
 * §1 — three tones and no more. Neutral is deliberately `--sep`/`--hint`
 * rather than an accent wash: "approved" and "done" are settled states and
 * must not compete with what is active.
 */
export type BadgeTone = "accent" | "neutral" | "danger";

// Spec 020 D2: `danger` (an error count, «просрочено») is an outline in the
// text colour, not red — red is the «Препятствие» status only.
const TONE: Record<BadgeTone, string> = {
  accent: "bg-p-surface-2 text-p-link",
  neutral: "bg-p-surface-2 text-p-text-muted",
  danger: "border border-p-border-strong text-p-text",
};

/** §4 — badge is radius 6, height 22. */
export function Badge({
  children,
  tone = "neutral",
  icon,
  className,
}: {
  children: ReactNode;
  tone?: BadgeTone;
  icon?: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex h-[22px] shrink-0 items-center gap-1 rounded-badge px-2 text-chip font-medium",
        TONE[tone],
        className,
      )}
    >
      {icon}
      {children}
    </span>
  );
}

const ICON = "h-3 w-3 shrink-0";

/* ── Task status (§7) ─────────────────────────────────────────────────── */

/**
 * Spec 020 D3: only «Сделано» is a status here. A task in progress sits under
 * the «В работе» heading and needs no pill; the rest are properties (`Mark`).
 */
export function TaskStatusBadge({ category }: { category: StatusCategory }) {
  switch (category) {
    case "done":
      return <Status tone="done" />;
    case "in_progress":
      return <Mark>в работе</Mark>;
    case "cancelled":
      return <Mark>отменена</Mark>;
    case "backlog":
    default:
      return <Mark>можно взять</Mark>;
  }
}

/**
 * §8 — the deadline badge. Overdue and today earn a badge; anything further
 * out is plain `--hint` text, because a badge on every row would flatten the
 * signal we actually want to carry.
 */
export function DueBadge({ due, now = new Date() }: { due: Date; now?: Date }) {
  const days = daysUntil(due, now);
  if (days < 0) {
    return (
      <Badge tone="danger" icon={<Clock className={ICON} aria-hidden="true" />}>
        просрочено · {formatDayMonth(due, now)}
      </Badge>
    );
  }
  if (days === 0) return <Badge tone="accent">сегодня</Badge>;
  return <span className="shrink-0 text-meta text-hint">до {formatDayMonth(due, now)}</span>;
}

/* ── Idea status (§7) ─────────────────────────────────────────────────── */

/**
 * Spec 020 D3: an open idea waits for a partner's decision («Ждёт»); one that
 * became work is «Сделано». A closed one carries its fact, not a status.
 */
export function IdeaStatusBadge({ status }: { status: IdeaStatus }) {
  switch (status) {
    case "new":
    case "reviewing":
    case "approved":
      return <Status tone="waiting" />;
    case "converted":
      return <Status tone="done" />;
    case "rejected":
      return <Mark>отклонена</Mark>;
    case "withdrawn":
      return <Mark>отозвана автором</Mark>;
  }
}

/* ── Tool kind (§7) ───────────────────────────────────────────────────── */

/**
 * §7 — outline chip in `--sep`/`--hint`, except `agent`, which is the primary
 * executor and therefore the only kind worth an accent.
 */
export function ToolKindChip({ kind }: { kind: ToolKind }) {
  const isAgent = kind === "agent";
  return (
    <span
      className={cn(
        "inline-flex h-5 shrink-0 items-center rounded-badge border px-1.5 text-micro",
        isAgent ? "border-accent text-accent" : "border-sep text-hint",
      )}
    >
      {kind}
    </span>
  );
}

/** Tool slug chips on a journal entry (§8). */
export function ToolSlugChip({ slug }: { slug: string }) {
  return (
    <span className="inline-flex h-5 shrink-0 items-center rounded-badge border border-sep px-1.5 text-micro text-hint">
      {slug}
    </span>
  );
}

/* ── Counters (§7) ────────────────────────────────────────────────────── */

/** §7 — paperclip plus a number, deliberately with no plate behind it. */
export function AttachmentCount({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span
      className="inline-flex shrink-0 items-center gap-1 text-chip text-hint"
      aria-label={`Вложений: ${count}`}
    >
      <Paperclip className="h-[13px] w-[13px]" aria-hidden="true" />
      {count}
    </span>
  );
}

/** §8 — "N ошибок" on a memory card that has erroneous journal entries. */
export function ErrorCountBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <Badge tone="danger" icon={<TriangleAlert className={ICON} aria-hidden="true" />}>
      {count} {pluralRu(count, "ошибка", "ошибки", "ошибок")}
    </Badge>
  );
}

/**
 * FEAT-025 / addendum §2 — "устарело" on a memory card whose precedent has
 * superseded journal entries. Neutral on purpose: it sits next to "N ошибок"
 * and must read as a *different kind of thing*, not as a milder warning.
 */
export function SupersededBadge() {
  return (
    <Badge tone="neutral" icon={<Clock className={ICON} aria-hidden="true" />}>
      устарело
    </Badge>
  );
}

/* ── Filter chips (§8, Ideas) ─────────────────────────────────────────── */

/**
 * §8 — the active filter is a solid `--btn` fill; the rest are outlines. Sits
 * at 28px with a collar taking the target to 44 (see `.hit-44`).
 */
export function FilterChip({
  children,
  active,
  onClick,
  count,
}: {
  children: ReactNode;
  active?: boolean;
  onClick?: () => void;
  count?: number;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "hit-44 press-surface inline-flex h-7 shrink-0 items-center gap-1 rounded-badge px-2.5 text-meta",
        active ? "bg-p-text text-p-bg font-semibold" : "border border-p-border text-p-text-muted",
      )}
    >
      {children}
      {count != null && <span className={active ? "opacity-80" : ""}>{count}</span>}
    </button>
  );
}

/**
 * §7 — "done" without green. Three signals at once: this icon, a line-through
 * on the title, and .6 opacity on the row. The accent stays reserved for what
 * is live right now.
 */
export function DoneIcon({ className }: { className?: string }) {
  return (
    <CircleCheckBig
      className={cn("h-[18px] w-[18px] shrink-0 text-hint", className)}
      aria-hidden="true"
    />
  );
}
